import React from "react";
import update from "immutability-helper";
import client from "lib/apolloClient/client";
import ColumnInfoTooltip from "../components/commonComponents/ColumnInfoTooltip";
import { getDefaultOrderBy } from "Utils/GetFormattedStaffName";
import {
  handleAdminSidebarBackButtonClick,
  handleAdminSidebarMenuItemClick,
} from "Login/routes/Administrator/modules/AdministratorModule";

import { setToastMsg } from "Login/modules/LoginModule/LoginActions";

import {
  updateDefaultReviewerMutation,
  updateStaffReviewerMutation,
} from "./ReviewerSetupMutation";

import { DESIGNATION_CONSTANTS_MAP } from "modules/Services";

let store;
import("store/createStore").then(importedModule => {
  store = importedModule.store;
});
import i18next from "i18next";
import {
  getOrganizationId,
  getSelectedCurriculumId,
} from "modules/CommonSelectors";
import {
  getDefaultReviewerSettingsQuery,
  getStaffRoleAccessFeedQuery,
} from "./ReviewerSetupQueries";
import _ from "lodash";
import { getFormattedUserName } from "Utils/GetFormattedUserName";

// Constants
export const NAME = "reviewerSetup";

export const UPDATE_STAFF_NAME_ORDER = "UPDATE_STAFF_NAME_ORDER" + " " + NAME;
export const SET_DEFAULT_REVIEWER_FOR_STAFF =
  "SET_DEFAULT_REVIEWER_FOR_STAFF" + " " + NAME;
export const UPDATE_REVIEWER_FOR_STAFF =
  "UPDATE_REVIEWER_FOR_STAFF" + " " + NAME;
export const UPDATE_REVIEWER_SELECTION_STATE =
  "UPDATE_REVIEWER_SELECTION_STATE" + " " + NAME;
export const BULK_SELECT_STAFF = "BULK_SELECT_STAFF" + " " + NAME;
export const BULK_SELECT_REVIEWER = "BULK_SELECT_REVIEWER" + " " + NAME;
export const BULK_APPLY_REVIEWER_PERMISSION =
  "BULK_APPLY_REVIEWER_PERMISSION" + " " + NAME;

export const SET_EDIT_MODE = "SET_EDIT_MODE" + " " + NAME;
export const SET_DISCARD_DIALOG = "SET_DISCARD_DIALOG" + " " + NAME;

export const REVIEWER_PERMISSIONS = t => {
  return [
    {
      label: t("journal:create_only"),
      key: "CREATE",
    },
    {
      label: t("journal:create_and_publish"),
      key: "CREATE_AND_PUBLISH",
    },
  ];
};

export const REVIEWER_ROLES = t => {
  return [
    {
      label: t("common:all"),
      key: "all",
    },
    {
      label: t("common:teacher"),
      key: "teacher",
    },
    {
      label: t("common:admin"),
      key: "admin",
    },
  ];
};

export const REVIEWER_SETUP_HEADER_COLUMNS = (t, isEditMode) => {
  const columnList = [
    {
      id: "name",
      key: "name",
      locale: "common:teacher_plural",
      entity: "name",
      hasSortingEnabled: !isEditMode,
      sortType: !isEditMode ? "dropdown" : null,
      sortByOptions: !isEditMode ? USER_NAME_SORT_OPTIONS : [],
      value: "name",
      type: "CUSTOM",
      isSticky: true,
      colSpan: 1,
      HeaderRightComponent: () => <div></div>,
      headerRightComponentStyle: { marginLeft: "auto" },
      style: {
        width: "268px",
      },
    },
    {
      id: "role",
      key: "role",
      locale: "journal:display_role",
      entity: "DISPLAY_ROLE",
      value: "role",
      type: "TEXTCOLUMN",
      hasSortingEnabled: !isEditMode,
      sortType: !isEditMode ? "toggle" : "none",
      colSpan: 1,
      HeaderRightComponent: () => <div></div>,
      headerRightComponentStyle: { marginLeft: "auto" },
      style: {
        borderRight: "1px solid #EBEBEB",
        minWidth: "160px",
      },
    },
    {
      id: "courses",
      key: "courses",
      locale: "common:class_plural",
      entity: "CLASSES",
      value: "courses",
      type: "CUSTOM",
      style: {
        maxWidth: "240px",
      },
    },
    {
      id: "permission",
      key: "permission",
      locale: "common:permission",
      entity: "PERMISSIONS",
      hasSortingEnabled: !isEditMode,
      sortType: !isEditMode ? "toggle" : "none",
      value: "permission",
      type: "CUSTOM",
      style: {
        minWidth: "200px",
      },
      headerRightComponentStyle: { marginLeft: "auto" },
      HeaderRightComponent: () => (
        <ColumnInfoTooltip tooltip={t("journal:permission_header_tooltip")} />
      ),
    },
    {
      id: "reviewers",
      key: "reviewers",
      locale: "journal:reviwers",
      headerTooltipLocale: "journal:reviewer_header_tooltip",
      entity: "reviewers",
      value: "reviewers",
      type: "CUSTOM",
      style: {
        minWidth: "288px",
      },
      headerRightComponentStyle: { marginLeft: "auto" },
      HeaderRightComponent: () => (
        <ColumnInfoTooltip tooltip={t("journal:reviewer_header_tooltip")} />
      ),
    },
  ];
  return columnList;
};

// This constant was copied from SchoolSetupModule.js to avoid circular dependency issue
export const USER_NAME_SORT_OPTIONS = [
  {
    key: "FIRSTNAME_AZ",
    label: "common:first_name_az",
    sortEntity: "FIRST_NAME",
    direction: "ASC",
  },
  {
    key: "FIRSTNAME_ZA",
    label: "common:first_name_za",
    sortEntity: "FIRST_NAME",
    direction: "DESC",
  },
  {
    key: "LASTNAME_AZ",
    label: "common:last_name_az",
    sortEntity: "LAST_NAME",
    direction: "ASC",
  },
  {
    key: "LASTNAME_ZA",
    label: "common:last_name_za",
    sortEntity: "LAST_NAME",
    direction: "DESC",
  },
];

export const getDefaultSortOrder = () => ({
  NAME: {
    menuItemEntity: getDefaultOrderBy(),
    direction: "ASC",
  },
});

export const setEditMode = data => {
  return {
    type: SET_EDIT_MODE,
    payload: data,
  };
};

export const setDiscardDialog = data => {
  return {
    type: SET_DISCARD_DIALOG,
    payload: data,
  };
};

export const reviewerSetupLeaveInterceptor = ({ destinationNode, source }) => {
  const state = store?.getState() ?? {};
  const dispatch = store?.dispatch;
  const isEditMode = _.get(state, "reviewerSetup.isEditMode");
  if (isEditMode) {
    const onDiscardConfirm = () => {
      if (source === "SIDEBAR_MENU") {
        const t = i18next.t;
        dispatch(
          handleAdminSidebarMenuItemClick({ t, key: destinationNode.uid })
        );
        return;
      }
      if (source === "SIDEBAR_BACK") {
        dispatch(handleAdminSidebarBackButtonClick());
        return;
      }
    };
    dispatch({
      type: SET_DISCARD_DIALOG,
      payload: {
        showDiscardDialog: store?.getState().reviewerSetup.isEditMode,
        onDiscardConfirm,
      },
    });
  }
  return !isEditMode;
};

export const getStaffPermsQueryVariables = ({ curriculumProgramId }) => ({
  organizationId: getOrganizationId(),
  filters: {
    designations: [DESIGNATION_CONSTANTS_MAP.TEACHER.id],
    curriculumProgramIds: [curriculumProgramId],
    archivalState: "ACTIVE",
  },
  permissionCurriculumId: curriculumProgramId,
  entityRelationMapFilters: {
    curriculumProgramId: curriculumProgramId,
    relatedEntityTypes: ["STAFF"],
    relationTypes: ["POST_REVIEWER"],
  },
  courseFilters: {
    curriculumProgramIds: [curriculumProgramId],
    archivalState: "ACTIVE",
    isDemo: false,
  },
});

export const getStaffRoleAccessFeedFromCache = variables => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getStaffRoleAccessFeedQuery,
      variables: variables,
    });
    return queryData;
  } catch (error) {
    return {};
  }
};

export const getDefaultReviewerSettingsFromCache = (
  variables,
  organizationId
) => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getDefaultReviewerSettingsQuery,
      variables: variables,
      organizationId,
    });
    return queryData;
  } catch (error) {
    return {};
  }
};

export const getStaffPermission = staffNode => {
  const permissions = JSON.parse(
    _.first(staffNode.permissions.configuration.configurations)?.value
  );
  return permissions;
};

export const getReviewersFromUserRelation = userRelationMap => {
  const reviewers = [];
  for (const edge of userRelationMap.edges) {
    const itr = edge?.node?.relatedEntity;
    const user = {
      name: getFormattedUserName({ user: itr, type: "staff" }),
      id: itr.id,
      isArchived: itr?.isArchived,
      profileImage: itr?.profileImage,
    };
    reviewers.push(user);
  }
  return reviewers;
};

export const SORT_BY_ENTITY = {
  FIRST_NAME: "FIRST_NAME",
  LAST_NAME: "LAST_NAME",
  DISPLAY_ROLE: "DISPLAY_ROLE",
  PERMISSION: "PERMISSIONS",
};

export const getPermissionString = permission => {
  if (!_.includes(permission, "PUBLISH")) {
    return "create_only";
  } else {
    return "create_and_publish";
  }
};

export const createReviewerSetupFeedData = staffEdges => {
  const updatesStaffEdges = [];
  _.forEach(staffEdges, staff => {
    const staffNodeData = {
      name: getFormattedUserName({ user: staff?.node }),
      id: staff.node.id,
      profileImage: staff.node.profileImage,
    };
    updatesStaffEdges.push(staffNodeData);
  });
  return updatesStaffEdges;
};

export const reviewerSetupReducer = (state, action) => {
  const { type, payload } = action;
  const updatedState = _.cloneDeep(state);
  switch (type) {
    case SET_DEFAULT_REVIEWER_FOR_STAFF: {
      const { id: staffId, value, setDeleteUserMap, deleteUserMap } = payload;

      const staffIndex = updatedState.findIndex(staff => staff.id === staffId);
      const previousReviewerData = _.map(
        updatedState[staffIndex]?.reviewers,
        "id"
      );
      const changedDeleteUserMap = _.cloneDeep(deleteUserMap);
      _.set(updatedState, `[${staffIndex}].permission`, value);
      if (value === "create_only") {
        _.set(updatedState, `[${staffIndex}].reviewers`, []);
      } else {
        const deletedReviewers = _.difference(previousReviewerData, value);

        if (_.size(deletedReviewers)) {
          _.forEach(deletedReviewers, user => {
            changedDeleteUserMap.push({
              staffId: staffId,
              reviewers: user,
            });
          });
        }

        _.size(deletedReviewers) && setDeleteUserMap(changedDeleteUserMap);

        _.set(updatedState, `[${staffIndex}].reviewers`, undefined);
      }
      return updatedState;
    }
    case UPDATE_REVIEWER_FOR_STAFF: {
      const {
        id: staffId,
        value,
        reviewerOptions,
        setDeleteUserMap,
        setAddUserMap,
        deleteUserMap,
        addUserMap,
      } = payload;

      const staffIndex = updatedState.findIndex(staff => staff.id === staffId);
      const previousReviewerData = _.map(
        updatedState[staffIndex]?.reviewers,
        "id"
      );
      const reviewers = _.filter(reviewerOptions, reviewer =>
        _.includes(value, reviewer.id)
      );
      _.set(updatedState, `[${staffIndex}].reviewers`, reviewers);
      const addedReviewers = _.difference(value, previousReviewerData);
      const deletedReviewers = _.difference(previousReviewerData, value);
      const changedAddUserMap = _.cloneDeep(addUserMap);
      const changedDeleteUserMap = _.cloneDeep(deleteUserMap);
      _.size(addedReviewers) &&
        changedAddUserMap.push({ staffId: staffId, reviewers: addedReviewers });
      _.size(deletedReviewers) &&
        changedDeleteUserMap.push({
          staffId: staffId,
          reviewers: deletedReviewers,
        });
      _.size(changedAddUserMap) && setAddUserMap(changedAddUserMap);
      _.size(changedDeleteUserMap) && setDeleteUserMap(changedDeleteUserMap);
      return updatedState;
    }
    case UPDATE_REVIEWER_SELECTION_STATE: {
      const { value } = payload;

      _.forEach(updatedState, staff => {
        if (_.includes(value, staff.id)) {
          staff.isSelected = true;
        } else {
          staff.isSelected = false;
        }
      });
      return updatedState;
    }
    case BULK_SELECT_STAFF: {
      const { value } = payload;
      _.forEach(updatedState, staff => {
        staff.isSelected = value;
      });
      return updatedState;
    }
    case BULK_SELECT_REVIEWER: {
      const {
        value,
        addUserMap,
        deleteUserMap,
        setAddUserMap,
        setDeleteUserMap,
        reviewerOptions,
      } = payload;

      const reviewers = _.filter(reviewerOptions, reviewer =>
        _.includes(value, reviewer.id)
      );
      const changedAddUserMap = _.cloneDeep(addUserMap);
      const changedDeleteUserMap = _.cloneDeep(deleteUserMap);
      const selectedStaffMembers = _.filter(updatedState, { isSelected: true });
      _.forEach(selectedStaffMembers, staff => {
        if (staff.isSelected) {
          const previousReviewerData = _.map(staff?.reviewers, "id");
          const addedReviewers = _.difference(value, previousReviewerData);
          const deletedReviewers = _.difference(previousReviewerData, value);

          addedReviewers &&
            changedAddUserMap.push({
              staffId: staff.id,
              reviewers: addedReviewers,
            });
          deletedReviewers &&
            changedDeleteUserMap.push({
              staffId: staff.id,
              reviewers: deletedReviewers,
            });
          _.size(changedAddUserMap) && setAddUserMap(changedAddUserMap);
          _.size(changedDeleteUserMap) &&
            setDeleteUserMap(changedDeleteUserMap);
          staff.reviewers = reviewers;
          staff.permission = "create_only";

          // delete staff.cellStyles;
        }
      });
      return updatedState;
    }
    case BULK_APPLY_REVIEWER_PERMISSION: {
      const {
        value,
        setDeleteUserMap,
        setAddUserMap,
        deleteUserMap,
        addUserMap,
      } = payload;
      const changedAddUserMap = _.cloneDeep(addUserMap);
      const changedDeleteUserMap = _.cloneDeep(deleteUserMap);
      _.forEach(updatedState, staff => {
        if (staff.isSelected) {
          const staffReviewers = staff?.reviewers;
          if (_.size(staffReviewers)) {
            changedDeleteUserMap.push({
              staffId: staff.id,
              reviewers: staffReviewers,
            });
          }
          staff.permission = value;
          if (value === "CREATE_AND_PUBLISH") {
            staff.reviewers = undefined;
          } else {
            staff.reviewers = [];
          }
        }
        setDeleteUserMap(changedDeleteUserMap);
        setAddUserMap(changedAddUserMap);
      });
      return updatedState;
    }

    default: {
      return updatedState;
    }
  }
};

const createUserRelationMap = ({ deleteUserMap, addUserMap }) => {
  const addRelationMap = [];
  _.forEach(addUserMap, userMap => {
    const { staffId, reviewers } = userMap;
    if (_.size(reviewers)) {
      _.forEach(reviewers, reviewer => {
        addRelationMap.push({
          entityId: staffId,
          relatedEntityId: reviewer,
          relationType: "POST_REVIEWER",
          entityType: "STAFF",
          relatedEntityType: "STAFF",
        });
      });
    }
  });

  const deleteRelationMap = [];
  _.forEach(deleteUserMap, userMap => {
    const { staffId, reviewers } = userMap;
    if (_.size(reviewers) && _.isArray(reviewers)) {
      _.forEach(reviewers, reviewer => {
        deleteRelationMap.push({
          entityId: staffId,
          relatedEntityId: _.has(reviewer, "id") ? reviewer?.id : reviewer,
          relationType: "POST_REVIEWER",
          entityType: "STAFF",
          relatedEntityType: "STAFF",
        });
      });
    } else if (_.size(deleteUserMap) && _.size(reviewers)) {
      deleteRelationMap.push({
        entityId: staffId,
        relatedEntityId: reviewers,
        relationType: "POST_REVIEWER",
        entityType: "STAFF",
        relatedEntityType: "STAFF",
      });
    }
  });
  return { addRelationMap, deleteRelationMap };
};

export const updateStaffReviewerFeed = payload => {
  const {
    deleteUserMap,
    addUserMap,
    setDeleteUserMap,
    setAddUserMap,
  } = payload;

  const { addRelationMap, deleteRelationMap } = createUserRelationMap({
    deleteUserMap,
    addUserMap,
  });
  return async (dispatch, getState) => {
    const curriculumProgramId = getSelectedCurriculumId(getState());

    try {
      if (_.size(addRelationMap) || _.size(deleteRelationMap)) {
        await client.mutate({
          mutation: updateStaffReviewerMutation,
          variables: {
            input: {
              curriculumProgramId,
              ...(_.size(addRelationMap) ? { addRelationMap } : {}),
              ...(_.size(deleteRelationMap)
                ? { removeRelationMap: deleteRelationMap }
                : {}),
            },
          },
          optimisticResponse: {
            __typename: "Mutation",
            platform: {
              __typename: "PlatformMutations",
              entityRelationMapBulkAction: {
                isSuccess: true,
                warning: null,
                __typename: "EntityRelationMapGenericResponse",
              },
            },
          },
          awaitRefetchQueries: true,
          refetchQueries: [
            {
              query: getStaffRoleAccessFeedQuery,
              variables: getStaffPermsQueryVariables({ curriculumProgramId }),
            },
          ],
          update: (cache, { data }) => {
            const { platform } = data;
            const { entityRelationMapBulkAction } = platform;
            const { isSuccess } = entityRelationMapBulkAction;
            isSuccess && setDeleteUserMap([]) && setAddUserMap([]);
          },
        });
      }
    } catch (err) {
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

const formatInputForDefaultReviewerSetting = ({
  defaultReviewerSetting,
  defaultReviewers,
  curriculumId,
}) => {
  const updateDefaultReviewerSetting =
    defaultReviewerSetting === "CREATE" ? ["CREATE"] : ["CREATE", "PUBLISH"];
  return [
    {
      name: "POST_REVIEW_DEFAULT_SETTING",
      value: JSON.stringify(updateDefaultReviewerSetting),
      curriculumId,
    },
    {
      name: "POST_DEFAULT_REVIEWERS",
      value: JSON.stringify(defaultReviewers),
      curriculumId,
    },
  ];
};

export const updateDefaultReviewerSetting = payload => {
  const { defaultReviewerSetting, defaultReviewers } = payload;

  return async (dispatch, getState) => {
    const curriculumId = getSelectedCurriculumId(getState());
    const input = formatInputForDefaultReviewerSetting({
      defaultReviewerSetting,
      defaultReviewers,
      curriculumId,
    });

    try {
      await client.mutate({
        mutation: updateDefaultReviewerMutation,
        variables: {
          input: input,
        },
      });
      dispatch(
        setToastMsg({
          msg: "journal:default_permission_updated",
          type: "success",
          position: "toast-bottom-left",
        })
      );
    } catch (err) {
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};
// Actions creators

export const updateStaffNameSortOrder = data => {
  return {
    data,
    type: UPDATE_STAFF_NAME_ORDER,
  };
};

// Reducer Handlers
const REDUCER_HANDLERS = {
  [UPDATE_STAFF_NAME_ORDER]: (state, action) => {
    const { data } = action;
    const { id, sortDirection, menuItemEntity } = data;
    if (id === null) {
      const defaultSortOrder = getDefaultSortOrder();
      //resetting sorting
      return update(state, {
        sortFields: { $set: defaultSortOrder },
      });
    }

    const updatedSort = {
      [id]: { direction: sortDirection, menuItemEntity },
    };
    return update(state, {
      sortFields: {
        $set: updatedSort,
      },
    });
  },
  [SET_EDIT_MODE]: (state, action) => {
    state = update(state, {
      isEditMode: { $set: action.payload },
    });
    return state;
  },
  [SET_DISCARD_DIALOG]: (state, action) => {
    const { payload } = action;
    if (_.isEmpty(payload) || _.isUndefined(payload)) {
      return update(state, {
        discardDialog: { $set: initialState.discardDialog },
      });
    }
    state = update(state, {
      discardDialog: { $set: payload },
    });
    return state;
  },
};

export const getMergedFeedDataWithDiff = (
  feedList,
  sortedFeedList,
  isSearchTextPresent
) => {
  const feedListMap = _.keyBy(feedList, "id");
  const sortedFeedListMap = _.keyBy(sortedFeedList, "id");
  const merged = [];

  _.forEach(feedList, item => {
    const key = item.id;
    if (_.has(sortedFeedListMap, key)) {
      const mergedItem = _.merge({}, item, _.get(sortedFeedListMap, key));

      mergedItem.permission = _.get(sortedFeedListMap, key).permission;
      mergedItem.reviewers = _.get(sortedFeedListMap, key)?.reviewers;
      merged.push(mergedItem);
    } else {
      merged.push(item);
    }
  });

  if (!isSearchTextPresent) {
    _.forEach(sortedFeedListMap, (value, key) => {
      if (!_.has(feedListMap, key)) {
        merged.push(value);
      }
    });
  }

  return merged;
};
// Initial State and export Reducer
const initialState = {
  sortFields: {},
  isEditMode: false,
  discardDialog: { showDiscardDialog: false, onDiscardConfirm: () => {} },
};

export default function myReducer(state = initialState, action) {
  const handler = REDUCER_HANDLERS[action.type];
  return handler ? handler(state, action) : state;
}
